import { type ReactNode, useRef } from 'react';

import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE } from '@/lib/motion-tokens';
import { cn } from '@/lib/utils';

export interface PointerParallaxProps {
  children: ReactNode;
  className?: string;
  /** Max travel (px) at full pointer offset for a layer of depth 1. */
  strength?: number;
}

/**
 * Layered pointer parallax. Any descendant marked `data-parallax` drifts with
 * the cursor; its `data-depth` (default 1) scales the travel — set larger depths
 * on foreground layers, small on background plates for a premium spatial feel.
 * Disabled on touch and reduced motion.
 *
 * @example
 * <PointerParallax>
 *   <img data-parallax data-depth="0.4" ... />
 *   <h2 data-parallax data-depth="1.2">…</h2>
 * </PointerParallax>
 */
export function PointerParallax({ children, className, strength = 24 }: PointerParallaxProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || reduced) return;
      if (window.matchMedia('(hover: none)').matches) return;

      const layers = gsap.utils.toArray<HTMLElement>('[data-parallax]', el);
      const movers = layers.map((layer) => {
        const depth = parseFloat(layer.dataset.depth ?? '1') || 1;
        const xTo = gsap.quickTo(layer, 'x', { duration: DURATION.slow, ease: GSAP_EASE.out });
        const yTo = gsap.quickTo(layer, 'y', { duration: DURATION.slow, ease: GSAP_EASE.out });
        return { depth, xTo, yTo };
      });

      const onMove = (event: PointerEvent) => {
        const rect = el.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width - 0.5;
        const py = (event.clientY - rect.top) / rect.height - 0.5;
        for (const m of movers) {
          m.xTo(px * strength * m.depth);
          m.yTo(py * strength * m.depth);
        }
      };
      const onLeave = () => {
        for (const m of movers) {
          m.xTo(0);
          m.yTo(0);
        }
      };

      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      return () => {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
      };
    },
    { scope: ref, dependencies: [reduced, strength] },
  );

  return (
    <div ref={ref} className={cn('relative', className)}>
      {children}
    </div>
  );
}
